import notificationCampaignApi from '@/api/notification-campaign'
import store from '@/store'
import { generateFormHandlerMutations } from '@/helpers/forms'
import { notificationLanguages, saveErrorText } from '@/helpers/notifications'

const emptyTexts = () => notificationLanguages.map(({ code }) => ({ language: code, title: '', body: '' }))

const getDefaultState = () => ({
  id: 0,
  name: '',

  scheduledAt: null,
  sendNow: false,
  toApp: true,
  toBrowser: false,
  link: '',
  imageUrl: '',
  texts: emptyTexts(),

  groupCriteriaIds: [],
  customerIds: [],

  fileCustomerIds: [],

  phoneNumbers: [],

  estimate: null,
  estimating: false,

  errors: {},
})

const state = getDefaultState()

const getters = {}

const audience = () => ({
  toApp: state.toApp,
  toBrowser: state.toBrowser,
  groupCriteriaIds: state.toApp ? state.groupCriteriaIds : [],
  customerIds: state.toApp ? state.customerIds : [],
  fileCustomerIds: state.toApp ? state.fileCustomerIds : [],
  phoneNumbers: state.toApp ? state.phoneNumbers : [],
})

const payload = () => ({
  name: state.name,
  scheduledAt: state.sendNow ? null : state.scheduledAt,
  sendNow: state.sendNow,
  link: state.link || null,
  imageUrl: state.imageUrl || null,

  texts: state.texts.filter(x => x.title || x.body),
  ...audience(),
})

const actions = {
  fetchById({ commit }, id) {
    commit('FORM_RESET')

    return notificationCampaignApi.getById(id)
      .then(({ data }) => commit('FETCHED', data))
      .catch(error => {
        store.dispatch('message/error', 'Не удалось открыть рассылку')
        throw error
      })
  },
  create({ commit }) {
    return notificationCampaignApi.create(payload())
      .then(() => store.dispatch('message/success', 'Рассылка создана'))
      .catch(error => {
        commit('SAVE_FAILED', error.response?.data)
        throw error
      })
  },
  update({ commit }) {
    return notificationCampaignApi.update(state.id, payload())
      .then(() => store.dispatch('message/success', 'Рассылка сохранена'))
      .catch(error => {
        commit('SAVE_FAILED', error.response?.data)
        throw error
      })
  },
  estimate({ commit }) {
    commit('ESTIMATING')

    return notificationCampaignApi.estimate(audience())
      .then(({ data }) => commit('ESTIMATED', data))
      .catch(error => commit('ESTIMATE_FAILED', error.response?.data))
  },

  previewPhoneNumbers({ commit }, file) {
    if (!file) {
      commit('PHONES_PARSED', [])

      return Promise.resolve()
    }

    return notificationCampaignApi.previewPhones(file)
      .then(({ data }) => commit('PHONES_PARSED', data))
      .catch(() => commit('PHONES_PARSE_FAILED'))
  },
  resetForm({ commit }) {
    commit('FORM_RESET')
  },
}

const mutations = {
  FETCHED(state, data) {
    Object.assign(state, {
      id: data.id,
      name: data.name,
      scheduledAt: data.scheduledAt,
      toApp: data.toApp,
      toBrowser: data.toBrowser,
      link: data.link || '',
      imageUrl: data.imageUrl || '',
      groupCriteriaIds: data.groupCriteriaIds || [],
      customerIds: data.customerIds || [],
      fileCustomerIds: data.fileCustomerIds || [],
    })

    state.texts = emptyTexts().map(empty => ({
      ...empty,
      ...(data.texts || []).find(x => x.language === empty.language),
    }))
  },
  SAVE_FAILED(state, data) {
    state.errors = data?.validationErrors || {}
    store.dispatch('message/error', saveErrorText(data, 'Не удалось сохранить рассылку'))
  },
  ESTIMATING(state) {
    state.estimating = true
  },
  ESTIMATED(state, data) {
    state.estimate = data
    state.estimating = false
  },
  ESTIMATE_FAILED(state, data) {
    state.estimate = null
    state.estimating = false
    store.dispatch('message/error', data?.message || 'Не удалось оценить аудиторию')
  },
  PHONES_PARSED(state, phoneNumbers) {
    state.phoneNumbers = phoneNumbers
  },
  PHONES_PARSE_FAILED(state) {
    state.phoneNumbers = []
    store.dispatch('message/error', 'Не удалось прочитать файл с номерами')
  },
  FORM_RESET(state) {
    Object.assign(state, getDefaultState())
  },
  ...generateFormHandlerMutations(state),
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
